import React, { useCallback, useEffect, useState } from 'react';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import { Star, Users, Clock3, X } from 'lucide-react';
import '../../styles/SecurityVisitorsPage.css';
import { listVisitors } from '../../services/guardService';

export default function SecurityVisitorsPage() {
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [selectedVisitor, setSelectedVisitor] = useState(null);

  const fetchVisitors = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const res = await listVisitors({ page, search, dateFrom, dateTo });
      if (res.success) {
        setVisitors(res.data || []);
        setPagination(res.pagination);
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Erreur lors du chargement des visiteurs');
    } finally {
      setLoading(false);
    }
  }, [page, search, dateFrom, dateTo]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchVisitors();
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [fetchVisitors]);

  return (
    <div className="vip-visitors-page">
      <div className="staff-vip-layout">
        <section className="staff-vip-list-card" style={{ gridColumn: '1 / -1' }}>
          <div className="staff-vip-list-header">
            <div>
              <h2>Liste des visiteurs</h2>
              <p>Recherche rapide pour le poste de sécurité.</p>
            </div>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="date"
                style={{ padding: '8.5px 14px', border: '1px solid var(--border)', borderRadius: '8px' }}
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                title="Date de début"
              />
              <input
                type="date"
                style={{ padding: '8.5px 14px', border: '1px solid var(--border)', borderRadius: '8px' }}
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                title="Date de fin"
              />
              <input
                type="text"
                style={{
                  padding: '8.5px 14px', border: '1px solid var(--border)', borderRadius: '8px', 
                  fontSize: '0.875rem', width: '300px'
                }}
                placeholder="Rechercher par nom, email ou téléphone..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
             <div style={{ background: 'var(--page-bg)', padding: '1rem', borderRadius: '8px', border: '1px solid var(--border)', flex: 1 }}>
               <h4 style={{ margin: '0 0 4px', color: 'var(--text-muted)' }}>Total visiteurs filtrés</h4>
               <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold' }}>{pagination ? pagination.total : 0}</p>
             </div>
             <div style={{ background: 'var(--page-bg)', padding: '1rem', borderRadius: '8px', border: '1px solid var(--border)', flex: 1 }}>
               <h4 style={{ margin: '0 0 4px', color: 'var(--text-muted)' }}>VIPs dans cette liste</h4>
               <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold', color: '#f59e0b' }}>
                 {visitors.filter(v => v.is_vip).length}
               </p>
             </div>
          </div>

          {error && <div className="error-banner">{error}</div>}

          {loading ? (
            <LoadingSkeleton lines={8} />
          ) : visitors.length === 0 ? (
            <div className="staff-vip-empty">Aucun visiteur trouvé.</div>
          ) : (
            <div className="staff-vip-list">
              {visitors.map((visitor) => (
                <article 
                  key={visitor.id} 
                  className="staff-vip-item"
                  onClick={() => setSelectedVisitor(visitor)}
                  style={{ cursor: 'pointer', transition: 'background-color 0.2s', ':hover': { backgroundColor: 'var(--color-sidebar-hover)' }, display: 'flex', flexDirection: 'column', gap: '12px' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1, minWidth: 0, paddingRight: '12px' }}>
                      <h3 style={{ margin: '0 0 4px 0', fontSize: '1.1rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{visitor.full_name}</h3>
                      <div style={{ display: 'flex', gap: '8px', fontSize: '0.875rem', color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                         {visitor.email && <span>{visitor.email}</span>}
                         {visitor.phone && <span title="Numéro de téléphone">📞 {visitor.phone}</span>}
                      </div>
                    </div>
                    <div style={{ flexShrink: 0 }}>
                      {visitor.is_vip ? (
                        <span className="staff-vip-pill" style={{ background: '#f59e0b', color: 'white', whiteSpace: 'nowrap' }}>
                          <Star size={14} fill="currentColor" /> VIP
                        </span>
                      ) : (
                        <span className="staff-vip-pill" style={{ background: 'var(--primary)', color: 'white', whiteSpace: 'nowrap' }}>
                          <Users size={14} fill="currentColor" /> Visiteur
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', padding: '8px', background: 'var(--surface)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                     <div>
                       <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block' }}>Visites</span>
                       <span style={{ fontWeight: '600' }}>{visitor.visit_count || 0}</span>
                     </div>
                     <div>
                       <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block' }}>Dernière visite</span>
                       <span style={{ fontWeight: '500', fontSize: '0.875rem' }}>
                         {visitor.last_visit ? new Date(visitor.last_visit).toLocaleDateString('fr-FR') : '—'}
                       </span>
                     </div>
                  </div>
                </article>
              ))}
            </div>
          )}

          {pagination && pagination.totalPages > 1 && (
            <div className="pagination" style={{
              display: 'flex', justifyContent: 'center', alignItems: 'center', 
              gap: '1rem', marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid var(--border)'
            }}>
              <button className="btn btn-secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>← Précédent</button>
              <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Page {page} / {pagination.totalPages}</span>
              <button className="btn btn-secondary" disabled={page >= pagination.totalPages} onClick={() => setPage(page + 1)}>Suivant →</button>
            </div>
          )}
        </section>
      </div>

      {selectedVisitor && (
        <div className="modal-overlay" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, padding: '1rem'
        }}>
          <div className="modal-content" style={{
            background: 'var(--surface)', width: '100%', maxWidth: '500px',
            borderRadius: '12px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }}>
            <div className="modal-header" style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--border)'
            }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '1.25rem', color: 'var(--text-main)' }}>Profil du visiteur</h2>
                <h3 style={{ margin: '4px 0 0', fontSize: '1rem', color: 'var(--text-muted)', fontWeight: 'normal' }}>
                  {selectedVisitor.full_name}
                  {selectedVisitor.is_vip && <span style={{ marginLeft: '8px', color: '#f59e0b', fontSize: '12px', fontWeight: 'bold' }}>VIP</span>}
                </h3>
              </div>
              <button onClick={() => setSelectedVisitor(null)} className="btn-icon" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
                <X size={20} color="var(--text-muted)" />
              </button>
            </div>
            
            <div className="modal-body" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '0.9375rem', color: 'var(--text-main)' }}>
              {selectedVisitor.email && <p style={{ margin: 0 }}><strong>Email:</strong> {selectedVisitor.email}</p>}
              {selectedVisitor.phone && <p style={{ margin: 0 }}><strong>Téléphone:</strong> {selectedVisitor.phone}</p>}
              
              <div style={{ marginTop: '8px', padding: '12px', background: 'var(--page-bg)', borderRadius: '8px', border: '1px solid var(--border)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <p style={{ margin: '0 0 4px', fontSize: '12px', color: 'var(--text-muted)' }}>Total des visites</p>
                  <p style={{ margin: 0, fontWeight: 'bold', fontSize: '1.125rem' }}>{selectedVisitor.visit_count || 0}</p>
                </div>
                <div>
                  <p style={{ margin: '0 0 4px', fontSize: '12px', color: 'var(--text-muted)' }}>Dernière visite</p>
                  <p style={{ margin: 0, fontWeight: '500' }}>
                    {selectedVisitor.last_visit ? new Date(selectedVisitor.last_visit).toLocaleDateString('fr-FR') : '—'}
                  </p>
                </div>
              </div>
              
              {selectedVisitor.next_expected_at && (
                <div className="staff-vip-time" style={{ marginTop: '8px' }}>
                  <Clock3 size={16} color="var(--primary)" />
                  <span style={{ color: 'var(--primary)', fontWeight: '500' }}>Prochaine venue prévue: {new Date(selectedVisitor.next_expected_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}